'use client';

import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Alert, Kbd, MetaDefault, SettingsRow } from '@terra/ui-design-system';
import { InformationCircleIcon } from '@terra/ui-icons';
import { ProxySettingsGroup } from './proxy-settings/ProxySettingsGroup';
import {
  buildShortcutCategories,
  type ShortcutKey,
} from './shortcuts/keyboard-shortcuts';

function ShortcutKeys({ keys }: { keys: ShortcutKey[] }) {
  const tShortcutsHelp = useTranslations('settings.shortcutsHelp');

  return (
    <HStack gap="1">
      {keys.map((keyItem, index) => (
        <HStack
          key={`${keyItem.kind === 'key' ? keyItem.key : keyItem.button}-${index}`}
          gap="1"
        >
          <Kbd size="sm">
            {keyItem.kind === 'key'
              ? keyItem.key
              : tShortcutsHelp(`keys.${keyItem.button}`)}
          </Kbd>
          {index < keys.length - 1 ? (
            <MetaDefault color="fg.muted">+</MetaDefault>
          ) : null}
        </HStack>
      ))}
    </HStack>
  );
}

export function ShortcutsTab() {
  const tShortcutsHelp = useTranslations('settings.shortcutsHelp');
  const isMac =
    typeof window !== 'undefined' && navigator.platform.includes('Mac');
  const categories = buildShortcutCategories({ isMac });

  return (
    <VStack gap={6} alignItems="stretch" w="full">
      {categories.map((category) => (
        <ProxySettingsGroup
          key={category.id}
          title={tShortcutsHelp(`categories.${category.id}`)}
        >
          {category.shortcuts.map((shortcut) => (
            <SettingsRow
              key={shortcut.id}
              label={tShortcutsHelp(`shortcuts.${shortcut.id}.label`)}
              description={
                shortcut.hasDescription
                  ? tShortcutsHelp(`shortcuts.${shortcut.id}.description`)
                  : undefined
              }
              control={<ShortcutKeys keys={shortcut.keys} />}
            />
          ))}
        </ProxySettingsGroup>
      ))}

      <Alert.Root>
        <Alert.Icon asChild>
          <InformationCircleIcon />
        </Alert.Icon>
        <Alert.Content>
          <Alert.Title>{tShortcutsHelp('behaviorTitle')}</Alert.Title>
          <Alert.Description>
            {tShortcutsHelp('behaviorNote')}
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    </VStack>
  );
}
